import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";


const Gallery = () => {
  const [selectedMedia, setSelectedMedia] = useState(null);
  const videoRefs = useRef([]);

  // 🔹 Ocultar header cuando se abre el modal
  useEffect(() => {
    const header = document.querySelector("header");
    if (header) header.style.display = selectedMedia ? "none" : "";
  }, [selectedMedia]);

  // 🔹 Galería principal
  const mediaItems = [
    {
      type: "videosGroup",
      title: "Nuestras instalaciones",
      alt: "Laboratorio Bienestar al Natural",
      thumbnail:"https://res.cloudinary.com/dmgtsa6kx/image/upload/v1763092679/imag15_dkxraz.jpg",
      videos: ["https://res.cloudinary.com/dmgtsa6kx/video/upload/v1763092896/video71_oz5vsj.mp4"
        ,"https://res.cloudinary.com/dmgtsa6kx/video/upload/v1763092934/video70_kjb64c.mp4"],
    },
    {
      type: "videosGroup",
      title: "Sobre Nosotros",
      alt: "Laboratorio Bienestar al Natural",
      thumbnail:"https://res.cloudinary.com/dmgtsa6kx/image/upload/v1763092680/imag14_wpd6tu.jpg",
      videos: ["https://res.cloudinary.com/dmgtsa6kx/video/upload/v1763092916/video72_lb3cfo.mp4"], // 🔸 Un solo video
    },
    {
      type: "videosGroup",
      title: "Testimonios",
      alt: "Laboratorio Bienestar al Natural",
      thumbnail:"https://res.cloudinary.com/dmgtsa6kx/image/upload/v1763092680/imag13_o98ycp.jpg",
      videos: ["https://res.cloudinary.com/dmgtsa6kx/video/upload/v1763092925/video66_vgpznw.mp4",
        "https://res.cloudinary.com/dmgtsa6kx/video/upload/v1763092938/video67_rjdr2u.mp4",
        "https://res.cloudinary.com/dmgtsa6kx/video/upload/v1763092884/video68_fdp1nz.mp4",
        "https://res.cloudinary.com/dmgtsa6kx/video/upload/v1763272000/video75_pmpzv2.mp4",
      ],
    },
  ];

  return (
    <section
      id="gallery"
      className="py-20 bg-gradient-to-br from-gray-50 to-green-50 relative z-10"
    >
      <div className="container mx-auto px-4">
        {/* 🔹 Título */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
         <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-6">
          <span className="text-green-600">Galería</span>
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Conoce más sobre Bienestar al Natural a través de nuestras imágenes
            y videos.
          </p>
        </motion.div>

        {/* 🔹 Cuadrícula principal */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {mediaItems.map((item, index) => (
            <motion.div
              key={index}
              className="relative group cursor-pointer overflow-hidden rounded-2xl shadow-lg"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              onClick={() => setSelectedMedia(item)}
            >
              <div className="aspect-video relative">
                <img
                  src={item.thumbnail}
                  alt={item.alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Play className="w-16 h-16 text-white" />
                </div>
                <div className="absolute top-4 right-4 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                  VIDEO
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <h3 className="text-white font-semibold">{item.title}</h3>
              </div>
            </motion.div>
          ))}
        </div>

        {/* 🔹 Modal de video */}
        <AnimatePresence>
          {selectedMedia && (
            <motion.div
              className="fixed inset-0 bg-gradient-to-br from-gray-50 to-green-50 z-50 flex flex-col items-start justify-start overflow-y-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedMedia(null)}
            >
              <motion.div
                className="relative w-full max-w-7xl mx-auto flex flex-col items-start justify-start min-h-screen"
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* 🔹 Botón cerrar */}
                <button
                  onClick={() => {
                    setSelectedMedia(null);
                    setTimeout(() => {
                      const gallerySection =
                        document.getElementById("gallery");
                      if (gallerySection) {
                        gallerySection.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }
                    }, 300);
                  }}
                  className="fixed top-6 right-8 text-gray-800 hover:text-gray-500 transition-colors z-50"
                >
                  <X className="w-8 h-8" />
                </button>

                {/* 🔹 Mostrar grupo de videos */}
                {selectedMedia.type === "videosGroup" && (
                  <>
                    {selectedMedia.videos.length === 1 ? (
                      // ✅ Un solo video: pantalla completa
                      <div className="flex justify-center items-start w-full h-screen bg-transparent">
                        <video
                          ref={(el) => (videoRefs.current[0] = el)}
                          src={selectedMedia.videos[0]}
                          className="w-auto max-w-[100vw] h-auto max-h-[100vh] object-contain"
                          playsInline
                          controls
                          autoPlay
                        />
                      </div>
                    ) : (
                      // ✅ Varios videos: cuadrícula dinámica y responsive
                      <div
                        className={`grid gap-6 p-6 w-full max-w-7xl mx-auto mt-16 ${
                          selectedMedia.videos.length <= 4
                            ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-2"
                            : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                        }`}
                      >
                        {selectedMedia.videos.map((src, i) => (
                          <div
                            key={i}
                            className="bg-white rounded-xl overflow-hidden aspect-video flex items-center justify-center shadow-md"
                          >
                            <video
                              ref={(el) => (videoRefs.current[i] = el)}
                              src={src}
                              className="w-full h-full object-contain"
                              playsInline
                              controls
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* 🔹 Título y botón volver */}
                <div className="mt-10 text-center pb-10 w-full">
                  <h3 className="text-gray-800 text-xl font-semibold text-center">
                    {selectedMedia.title}
                  </h3>
                  <button
                    onClick={() => {
                      setSelectedMedia(null);
                      setTimeout(() => {
                        const gallerySection =
                          document.getElementById("gallery");
                        if (gallerySection) {
                          gallerySection.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }
                      }, 300);
                    }}
                    className="mt-4 px-5 py-2 bg-green-600 text-white font-medium rounded-full hover:bg-green-700 transition-colors shadow-md"
                  >
                    ← Volver a Galería
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Gallery;
















